<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                     My Account                                       │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div class="rewards-my-account" v-if="loaded">

        <h3 v-if="!member" class="rewards-title">Please sign in to view your account details.</h3>

        <template v-else>
            <div :class="['account-info', { tierFree: !tierInfoExtended }]">

                <!-- Member Info -->
                <div class="account-balance">
                    <span>Miles Balance</span>
                    <a @click="navToHistory()"><strong>{{ account.milesBalance | formatNumber }}</strong></a>
                </div>

                <!-- Member Card -->
                <div v-if="account" class="card-container">
                    <div :class="['card', `${currentTierName.toLowerCase()}-tier` ]">
                        <div class="card-member-info">
                            <div class="member-name">{{ member.firstName }} {{ member.lastName }}</div>
                            <div class="member-tier">{{ currentTierName }}</div>
                            <div class="member-id">{{ account.id }}</div>
                            <date-value class="member-since" :value="account.memberSince" key="account.memberSince">Expired</date-value>
                        </div>
                    </div>
                    <p class="card-print">
                        <a @click="printCard">Print this card</a>
                    </p>
                </div>

                <!--Upcoming Flights-->
                <div v-if="upcomingFlights.length" class="upcoming-flights">
                    <h3>Your upcoming trips</h3>
                    <table>
                        <tr>
                            <th>PNR</th>
                            <th>Flight</th>
                            <th>Route</th>
                            <th>Flight Date</th>
                        </tr>
                        <tr v-for="(flight, n) in upcomingFlights" :class="flight.description.toLowerCase().replace(' ', '-')">
                            <!--<td><date-value :value="flight.entryDate" :key="`[${n}].entryDate`">-</date-value></td>-->
                            <td class="pnr-link">
                                    <template v-if="!flight.pnr">-</template>
                                <a v-else :href="lookupPNRLink(flight.pnr, flight.auth || member.lastName)">{{ flight.pnr }}</a>
                            </td>
                            <td>{{ (flight.flightNumber || '-').replace(/(\D+)(\d+)/, "$1-$2") }}</td>
                            <td>{{ flight.flightRoute || '-' }}</td>
                            <td><date-value :value="flight.firstDeparture" :key="`[${n}].firstDeparture`">-</date-value></td>
                            <!--<td>{{ flight.description || '-' }}</td>-->
                        </tr>
                    </table>
                </div>

                <!--No upcoming flights-->
                <div v-else class="upcoming-travel">
                    <h3 class="upcoming-travel-message">No upcoming flights found</h3>
                    <div class="upcoming-travel-links">
                        <a href="/bookings/members.aspx">Book a flight</a>
                        <a href="/bookings/Vues/flight_selection.aspx?SS=FFR&PA=1">Redeem Miles</a>
                    </div>
                </div>
            </div>

            <!-- Tier Details -->
            <template v-if="tierInfoExtended && REWARDS.HasTiers">

                <div class="tier-details">
                    <div v-for="(tier, tierName, index) in tierInfoExtended" :class="['tier', `${tier.tierName.toLowerCase()}-tier`]" :key="tier.id">
                        <div :class="['tier-name', {'active-tier': isActiveTier(tier)}]">
                            <span v-if="isActiveTier(tier)" class="tier-active">
                                <span>Active</span>
                            </span>
                            <h2>{{ tier.tierName }}</h2>
                            <span v-if="tier.tierMilesBalance > tier.requiredMiles || tier.tierName == account.cardStatus" class="achieved">Achieved</span>
                            <p v-if="isActiveTier(tier) && account.tierExpiration" class="tier-expiration">Expires: {{ account.tierExpiration | ddMMMyyyy }}</p>
                        </div>
                        <div class="tier-info">
                            <div class="req-and-eligibility">
                                <!--Requirements-->
                                <Transition name="fade">
                                    <div v-if="tierProgress(tier) == 0 || showExtraDetails(tier.isOpen)" class="requirements">
                                        <h4>Requirements</h4>
                                        <template v-if="tier.requiredMiles == 0">
                                            None. Start earning today!
                                        </template>
                                        <template v-else>
                                            Members must earn {{ tier.requiredMiles | formatNumber }} miles
                                            <template v-if="tier.requiredFlights">
                                                or {{ tier.requiredFlights }} flights
                                            </template>
                                            in {{ tier.requiredMonths }} months
                                            to qualify for {{ tier.tierName }} status
                                        </template>
                                    </div>
                                </Transition>

                                <!--Eligibility-->
                                <Transition name="fade">

                                    <div v-if="!tier.requiredMiles <= 0 && showExtraDetails(tier.isOpen)" class="eligibility">
                                        <h4>Eligibility</h4>
                                        <template v-if="tier.requiredMiles > 0">
                                            <template v-if="milesToAchieveTier(tier) > 0">
                                                Earn {{ milesToAchieveTier(tier) | formatNumber }}
                                            </template>
                                            <template v-else>
                                                Earn {{ milesToExtendTier(tier) | formatNumber }}
                                            </template>
                                        miles by <date-value :value="nextFallOffDate(tier)" key="nextFallOffDate(tier)" />
                                            <template v-if="tier.tierName == account.cardStatus || milesToAchieveTier(tier) <= 0">
                                            to keep this tier through <date-value :value="tierExtensionDate(tier)" key="tierExtensionDate(tier)" />
                                            </template>
                                            <template v-else>
                                                to qualify for {{ tier.tierName }} status
                                            </template>
                                        </template>
                                        <br />
                                        <template v-if="tier.requiredFlights > 0 && showExtraDetails(tier.isOpen)">
                                            <template v-if="flightsToAchieveTier(tier) > 0">
                                                Earn {{ flightsToAchieveTier(tier) | formatNumber }}
                                            </template>
                                            <template v-else>
                                                Earn {{ flightsToExtendTier(tier) | formatNumber }}
                                            </template>
                                        flights by <date-value :value="nextFallOffDate(tier)" key="nextFallOffDate(tier)" />
                                            <template v-if="tier.tierName == account.cardStatus">
                                            to keep this tier through <date-value :value="tierExtensionDate(tier)" key="tierExtensionDate(tier)" />
                                            </template>
                                            <template v-else>
                                                to qualify for {{ tier.tierName }} status
                                            </template>
                                        </template>

                                    </div>
                                    </Transition>
                            </div>


                            <!--Tier requirements-->
                            <div :id="`tier_${index}`" class="expanded-display">
                                <template v-if="tier.requiredMiles > 0 || tier.requiredFlights > 0">
                                    Members must earn {{ tier.requiredMiles | formatNumber }} miles

                                    <template v-if="tier.requiredFlights > 0">
                                        or {{ tier.requiredFlights }} flights
                                    </template>

                                    in {{ tier.requiredMonths }} months to qualify
                                </template>
                                <template v-else>
                                    Start earning today!
                                </template>
                            </div>

                            <!--Tier Progress Bar Data-->
                            <div class="tier-progress-bar-background" v-if="tierProgress(tier) != 0">
                                <div class="tier-progress-bar-info">
                                    <template v-if="milesToAchieveTier(tier) <= 0">
                                        {{ tier.tierName }} Tier Achieved!
                                    </template>
                                    <template v-else>
                                        <div class="box-left">
                                            <a @click="navToHistory({ months: tier.requiredMonths })" class="tier-miles-link">{{ tier.tierMilesBalance | formatNumber }} tier miles earned</a>
                                        </div>
                                        <div class="box-center">{{ tierProgress(tier) + "%" }}</div>
                                        <div class="box-right">
                                            <template v-if="milesToAchieveTier(tier) == 0">
                                                {{ tier.tierName }} Tier Achieved
                                            </template>
                                            <template v-else>
                                                <template v-if="tier.requiredMiles > 0">
                                                    <template v-if="milesToAchieveTier(tier) > 0">
                                                        Earn {{ milesToAchieveTier(tier) | formatNumber }}
                                                    </template>
                                                    <template v-else>
                                                        Earn {{ milesToExtendTier(tier) | formatNumber }}
                                                    </template>
                                                    more miles by <date-value :value="nextFallOffDate(tier)" key="nextFallOffDate(tier)" />
                                                    <br />
                                                    <template v-if="tier.tierName == account.cardStatus || milesToAchieveTier(tier) <= 0">
                                                        to keep this tier through <date-value :value="tierExtensionDate(tier)" key="tierExtensionDate(tier)" />
                                                    </template>
                                                    <template v-else>
                                                        to qualify for {{ tier.tierName }} status
                                                    </template>
                                                </template>
                                            </template>
                                        </div>
                                    </template>
                                </div>

                                <!--Tier Progress Bar Visual-->
                                <div class="tier-progress-bar" :style="{width: tierProgress(tier) + '%'}"></div>


                            </div>

                        </div>

                        <div class="show-more-info" @click="toggleTierIsOpen(index)">
                            <template v-if="tier.isOpen">&and;</template>
                            <template v-else> &or;</template>
                        </div>
                    </div>
                </div>

                <div class="more-details-container" v-if="moreDetails">
                    <a @click="toggleExtraDetails()" class="more-details">
                        <template v-if="!extraDetails">
                            More details
                        </template>
                        <template v-else>
                            Less details
                        </template>
                    </a>
                </div>

                <div class="learn-more" v-if="REWARDS.AboutURL">
                    <a :href="REWARDS.AboutURL" target="_blank" class="learn-more-button">Learn more about Tier benefits</a>
                </div>

            </template>

        </template>

    </div>
</template>

<script>
    import { fetchJSON } from "@/helpers/fetch"
    import * as format from "@/helpers/format"
    import { isoDate, toDate } from "@/helpers/dates"

    export default {
        name: 'MyAccount',
        filters: { ...format },

        props: {
            memberDetails: Object
        },

        emits: ['loading', 'loaded', 'update:member-details'],

        data() {
            let { member, account, upcomingFlights } = this.memberDetails || {}
            let tierInfo = member && member.tierInfo
            if (tierInfo) tierInfo = tierInfo.map(obj => ({ ...obj, isOpen: false }))
            return {
                upcomingFlights,
                transactionHistory: null,
                tierInfo,
                member,
                account,

                extraDetails: false,
                isActive: true,

                loaded: false,
                today: new Date().toISOString() // use this value for tier calculations; allows us to change in VueDevTools if we want
            }
        },

        mounted() {
            let endpoints = [this.getTransactionHistory()]
            if (!this.memberDetails) endpoints.push(this.getMemberDetails())

            this.$emit('loading')

            Promise.all(endpoints)
                .then(() => {
                    this.loaded = true
                    this.$emit('loaded')
                })
                .catch(error => {
                    this.$emit('close')
                })
        },

        methods: {

            collapseToggle(id) {
                let element = document.getElementById(`tier_${id}`);
                if (element.style.display == "block") {
                    element.style.display = "none";
                } else {
                    element.style.display = "block";
                }
            },

            getMemberDetails() {
                return fetchJSON(`/api/members/GetMemberDetails?verbose=true`)
                    .then(data => {
                        this.tierInfo = data.member.tierInfo.map(obj => ({ ...obj, isOpen : false }));
                        this.account = data.account;
                        this.member = data.member;
                        this.upcomingFlights = data.upcomingFlights;

                        this.$emit('update:member-details', data)
                    })
            },

            getTransactionHistory() {
                return fetchJSON(`/api/members/GetTransactionHistory`)
                    .then(data => {
                        this.transactionHistory = data;
                    })
            },

            milesToAchieveTier(tier) {
                const requiredMiles = tier.requiredMiles - tier.tierMilesBalance;
                if (requiredMiles < 0) return 0;
                return requiredMiles;
            },

            flightsToAchieveTier(tier) {
                const requiredFlights = tier.requiredFlights - tier.tierFlightsBalance;

                if (requiredFlights < 0) {
                    return 0;
                }
                return requiredFlights;
            },

            nextFallOffDate(tier) {
                const firstFlight = this.getFirstFlight(tier);
                if (firstFlight) {
                    let firstFlightDate = new Date(firstFlight.entryDate);
                    return new Date(firstFlightDate.setMonth(firstFlightDate.getMonth() + tier.requiredMonths));
                } else {
                    //If user has no existing flights, calculate next date based on today's date.
                    let today = new Date(this.today);
                    return new Date(today.setMonth(today.getMonth() + tier.requiredMonths));
                }
            },

            eligibilityStartDate(tier) {
                let today = new Date(this.today);
                return new Date(today.setMonth(today.getMonth() - tier.requiredMonths));
            },

            eligibilityEndDate(tier) {
                return new Date(this.today);
            },

            contributingFlights(tier) {
                if (this.transactionHistory && this.transactionHistory.transactions) {
                    return this.transactionHistory.transactions
                        .filter(transaction => transaction.entryDate >= this.eligibilityStartDate(tier) && transaction.entryDate <= this.eligibilityEndDate(tier))
                        .filter(transaction => transaction.isTierMile)
                        .sort((a, b) => a.entryDate - b.entryDate);
                }
                return [];
            },

            tierRewardsEarned(tier) {
                return this.contributingFlights(tier).reduce((total, tier) => total + tier.rewards, 0);
            },

            getFirstFlight(tier) {
                return this.contributingFlights(tier)[0];
            },

            milesToExtendTier(tier) {
                const firstFlight = this.getFirstFlight(tier);
                if (firstFlight && firstFlight.rewards) {
                    if (firstFlight.rewards <= 0) return Math.abs(firstFlight.rewards);
                    return firstFlight.rewards;
                }
            },

            flightsToExtendTier(tier) {
                //We assume we've already reached this tier status.
                //To Keep it, we need to make sure we've reached the tier's required Flights

                let numFlights = tier.requiredFlights - tier.tierFlightsBalance;
                if (numFlights == 0) return 0;

                //Add 1 because firstFlight will be expiring.
                return Math.abs(numFlights) + 1;
            },

            tierExtensionDate(tier) {
                const nextFallOff = this.nextFallOffDate(tier);
                if (nextFallOff) {
                    return new Date(nextFallOff.setMonth(nextFallOff.getMonth() + tier.validMonths));
                }
            },

            isActiveTier(tier) {
                //If account's cardStatus is null, return true for first tier
                if (this.account.cardStatus == null && tier.tierName == this.tierInfo[0].tierName) return true;
                if (this.account.cardStatus == tier.tierName) return true;
                return false;
            },

            tierProgress(tier) {
                let balance = tier.tierMilesBalance || 0
                let required = tier.requiredMiles || 0

                if (isNaN(required) || required <= 0) return 100
                if (isNaN(balance) || balance <= 0) return 0

                if (balance >= required) return 100

                return Math.ceil(100 * (balance / required))
            },

            printCard() {
                //Todo:: Implement card print functionality
                javascript: window.print(); return false;
            },

            navToHistory(params) {
                //  Send by name - not by path - to be able to send params to the component
                this.$router.push({ name: 'TransactionHistory', params });
            },

            lookupPNRLink(pnr, auth) {
                if (pnr && auth) return `/bookings/view_reservation.aspx?pnr=${pnr}&auth=${auth}`
            },

            toggleTierIsOpen(index) {
                this.tierInfo[index].isOpen = !this.tierInfo[index].isOpen;
            },

            showExtraDetails(isOpen) {
                return isOpen;
            },

            toggleExtraDetails() {
                this.extraDetails = !this.extraDetails;
                this.tierInfo.forEach(tier => tier.isOpen = this.extraDetails);
            },
        },

        computed: {
            REWARDS() { return this.CONFIG.RewardsProgram || {} },

            tierInfoExtended() {
                return this.tierInfo && Object.fromEntries(this.tierInfo.map(tier => ({
                    ...tier,
                    contributingFlights: this.contributingFlights(tier),
                    getFirstFlight: this.getFirstFlight(tier),
                    tierMilesBalance: this.tierRewardsEarned(tier),
                    nextFallOffDate: this.nextFallOffDate(tier),
                    tierExtensionDate: this.tierExtensionDate(tier),
                    eligibilityStartDate: this.eligibilityStartDate(tier),
                    eligibilityEndDate: this.eligibilityEndDate(tier),
                    tierProgress: this.tierProgress(tier)
                }))
                    .map(tier => [tier.tierName, tier]))
            },

            greetingMessage() {
                var today = new Date();
                var userHour = today.getHours();
                let message = "Welcome";

                if (userHour < 12) message = "Good morning"
                else if (userHour < 18) message = "Good afternoon"
                else message = "Good evening"

                return message;
            },

            sortedTierList() {
                return (this.tierInfo || []).sort((a, b) => a.tierBonusPercentage - b.tierBonusPercentage);
            },

            currentTierName() {
                let currentTier = ({ ...this.account }).cardStatus
                if (currentTier) return currentTier

                let tierInfo = ({ ...this.member }).tierInfo
                if (tierInfo && tierInfo[0]) return tierInfo[0].tierName
            },

            currentTier() {
                return (this.tierInfo || []).filter(tier => tier.tierName == this.currentTierName)[0]
            },

            //Finds tierName of next tier from account owners current tier.
            nextTierName() {
                //Find where tierName exists in the array and return the next in line.
                const indexOfTier = this.sortedTierList.indexOf(this.currentTier);
                if (indexOfTier > -1 && indexOfTier < this.sortedTierList.length - 1) return this.sortedTierList[(indexOfTier + 1)].tierName;

                //If no higher tier exists, return current.
                return this.currentTierName;
            },

            moreDetails() {
                let sum = 0;
                for (let tier in this.member.tierInfo) {
                    if (this.member.tierInfo[tier].tierMilesBalance > 0) {
                        sum += this.member.tierInfo[tier].tierMilesBalance;
                    }
                }

                return sum > 0 ? true : false;

                // Alternatively:
                let total = this.member.tierInfo
                    .map(info => info.tierMilesBalance)
                    .filter(balance => balance > 0)
                    .reduce((balance, sum) => balance + sum, 0)

                return (total > 0)
            }
        }
    }
</script>